// NearbyBookingsPage — /collections/geolink/nearby
//
// Postcode-driven radius search across bookings + skip_jobs. Submitting
// the controls fires the search via React Query; results render in two
// places kept in sync via highlightedEncId:
//   - left panel: a sortable table of bookings + skip jobs
//   - right panel: a Leaflet map with coloured markers and a radius circle
//
// The Reschedule "action" for bookings opens the existing edit page in a
// new tab — there is no dedicated reschedule modal yet, and we don't
// duplicate logic to add one here. Skip-job rows route to the detail page.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Pencil, Search, ExternalLink } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { Button } from '../../../components/ui/Button/Button';
import { Table, type Column } from '../../../components/ui/Table/Table';
import { Input } from '../../../components/form/Input';
import { Select } from '../../../components/form/Select';
import { Checkbox } from '../../../components/form/Checkbox';
import { DatePicker } from '../../../components/form/DatePicker';
import { listDrivers } from '../../../api/drivers';
import { getNearbyResults, type GetNearbyParams } from '../../../api/geolink';
import { queryKeys } from '../../../api/queryKeys';
import { NearbyResultsMap } from './NearbyResultsMap';
import styles from './NearbyBookingsPage.module.css';

type ResultKind = 'booking' | 'skip_job';

interface ResultRow {
  enc_id: string;
  kind: ResultKind;
  distance_miles: number;
  date: string;
  customer: string;
  address: string;
  status: string;
  driver: string;
  price: number | null;
  raw_enc_id: string;
}

const RADIUS_OPTIONS = [5, 10, 20, 30, 50];

function isoToday(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function NearbyBookingsPage() {
  // -- Form state ---------------------------------------------------------
  const [postcode, setPostcode] = useState<string>('');
  const [radius, setRadius] = useState<number>(30);
  const [dateFrom, setDateFrom] = useState<string>(isoToday(0));
  const [dateTo, setDateTo] = useState<string>(isoToday(7));
  const [driverEncId, setDriverEncId] = useState<string>('');
  const [includeBookings, setIncludeBookings] = useState<boolean>(true);
  const [includeSkipJobs, setIncludeSkipJobs] = useState<boolean>(true);

  // -- Submitted params ---------------------------------------------------
  // Searches only fire when the user clicks "Search". We snapshot the
  // controls into `submitted` and React Query keys off that object so
  // changing controls doesn't refetch on every keystroke.
  const [submitted, setSubmitted] = useState<GetNearbyParams | null>(null);

  const [highlightedEncId, setHighlightedEncId] = useState<string | null>(null);

  const driversQuery = useQuery({
    queryKey: queryKeys.driversAdmin.list({ status: 'active', limit: 100 }),
    queryFn: () => listDrivers({ status: 'active', limit: 100, page: 1 }),
    staleTime: 60_000,
  });

  const driverOptions = useMemo(
    () => [
      { value: '', label: 'All drivers' },
      ...(driversQuery.data?.drivers ?? []).map((d) => ({
        value: d.enc_id,
        label: d.name,
      })),
    ],
    [driversQuery.data],
  );

  const nearbyQuery = useQuery({
    queryKey: queryKeys.geolink.nearby({
      postcode: submitted?.postcode ?? '',
      radius_miles: submitted?.radius_miles ?? null,
      date_from: submitted?.date_from ?? null,
      date_to: submitted?.date_to ?? null,
      driver_enc_id: submitted?.driver_enc_id ?? null,
      include_bookings: submitted?.include_bookings ?? null,
      include_skip_jobs: submitted?.include_skip_jobs ?? null,
    }),
    queryFn: () => getNearbyResults(submitted!),
    enabled: submitted !== null,
    staleTime: 30_000,
  });

  function handleSearch(): void {
    if (postcode.trim().length === 0) return;
    setSubmitted({
      postcode: postcode.trim(),
      radius_miles: radius,
      date_from: dateFrom,
      date_to: dateTo,
      driver_enc_id: driverEncId.length > 0 ? driverEncId : undefined,
      include_bookings: includeBookings,
      include_skip_jobs: includeSkipJobs,
    });
    setHighlightedEncId(null);
  }

  // Flatten bookings + skip jobs into one row set sortable by any column.
  const rows: ResultRow[] = useMemo(() => {
    if (!nearbyQuery.data) return [];
    const out: ResultRow[] = [];
    for (const b of nearbyQuery.data.bookings) {
      out.push({
        enc_id: b.enc_id,
        kind: 'booking',
        distance_miles: b.distance_miles,
        date: b.date,
        customer: b.customer_name,
        address: `${b.address} · ${b.postcode}`,
        status: b.status,
        driver: b.driver_name ?? '—',
        price: b.price,
        raw_enc_id: b.enc_id,
      });
    }
    for (const s of nearbyQuery.data.skip_jobs) {
      out.push({
        enc_id: s.enc_id,
        kind: 'skip_job',
        distance_miles: s.distance_miles,
        date: s.date ?? '—',
        customer: s.customer_name,
        address: `${s.address ?? '—'} · ${s.postcode}`,
        status: s.status,
        driver: s.driver_name ?? '—',
        price: null,
        raw_enc_id: s.enc_id,
      });
    }
    out.sort((a, b) => a.distance_miles - b.distance_miles);
    return out;
  }, [nearbyQuery.data]);

  const columns: Column<ResultRow>[] = [
    {
      key: 'distance_miles',
      header: 'Dist',
      sortable: true,
      width: '78px',
      render: (_v, row) => (
        <span className={styles.distance}>{row.distance_miles.toFixed(1)} mi</span>
      ),
    },
    { key: 'date', header: 'Date', sortable: true, width: '110px' },
    {
      key: 'customer',
      header: 'Customer',
      sortable: true,
      render: (_v, row) => (
        <div>
          <div>{row.customer}</div>
          <div className={styles.muted}>{row.address}</div>
        </div>
      ),
    },
    {
      key: 'kind',
      header: 'Type',
      sortable: true,
      width: '90px',
      render: (_v, row) => (
        <span className={`${styles.typeBadge} ${row.kind === 'booking' ? styles.typeBooking : styles.typeSkip}`}>
          {row.kind === 'booking' ? 'Booking' : 'Skip'}
        </span>
      ),
    },
    { key: 'status', header: 'Status', sortable: true, width: '110px' },
    { key: 'driver', header: 'Driver', sortable: true, width: '120px' },
    {
      key: 'price',
      header: 'Price',
      sortable: true,
      align: 'right',
      width: '80px',
      render: (_v, row) =>
        row.price === null ? <span className={styles.muted}>—</span> : `£${row.price.toFixed(2)}`,
    },
    {
      key: 'raw_enc_id',
      header: '',
      width: '60px',
      align: 'right',
      render: (_v, row) =>
        row.kind === 'booking' ? (
          (row.status === 'pending' || row.status === 'in_progress') ? (
            <Link
              to={`/collections/bookings/${encodeURIComponent(row.enc_id)}/edit`}
              aria-label={`Reschedule booking ${row.customer}`}
              title="Reschedule"
              onClick={(e) => e.stopPropagation()}
            >
              <Pencil size={16} aria-hidden />
            </Link>
          ) : null
        ) : (
          <Link
            to={`/skip/jobs/${encodeURIComponent(row.enc_id)}`}
            aria-label={`Open skip job ${row.customer}`}
            title="Open job"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink size={16} aria-hidden />
          </Link>
        ),
    },
  ];

  const bookingCount = nearbyQuery.data?.bookings.length ?? 0;
  const skipCount = nearbyQuery.data?.skip_jobs.length ?? 0;
  const showEmptyHelp =
    submitted !== null &&
    !nearbyQuery.isLoading &&
    !nearbyQuery.isError &&
    bookingCount === 0 &&
    skipCount === 0;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Nearby Bookings"
        subtitle="Find live work near a postcode across bookings and skip jobs"
      />

      <Card>
        <form
          className={styles.controls}
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
        >
          <Input
            label="Postcode"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            placeholder="e.g. SW1A 1AA"
            required
            autoComplete="postal-code"
          />
          <Select
            label="Radius"
            value={String(radius)}
            onChange={(e) => setRadius(Number(e.target.value))}
            options={RADIUS_OPTIONS.map((m) => ({ value: String(m), label: `${m} miles` }))}
          />
          <DatePicker
            label="From"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
          <DatePicker
            label="To"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
          <Select
            label="Driver"
            value={driverEncId}
            onChange={(e) => setDriverEncId(e.target.value)}
            options={driverOptions}
          />
          <div className={styles.toggleRow}>
            <Checkbox
              label="Bookings"
              checked={includeBookings}
              onChange={(e) => setIncludeBookings(e.target.checked)}
            />
            <Checkbox
              label="Skip Jobs"
              checked={includeSkipJobs}
              onChange={(e) => setIncludeSkipJobs(e.target.checked)}
            />
          </div>
          <div className={styles.searchRow}>
            <Button type="submit" icon={Search} loading={nearbyQuery.isFetching}>
              Search
            </Button>
          </div>
        </form>
      </Card>

      <div className={styles.layout}>
        <div className={styles.tablePanel}>
          {submitted !== null && (
            <div className={styles.summary}>
              {bookingCount} booking{bookingCount === 1 ? '' : 's'},{' '}
              {skipCount} skip job{skipCount === 1 ? '' : 's'} within{' '}
              {submitted.radius_miles ?? 30} miles
              {nearbyQuery.data
                ? ` of ${nearbyQuery.data.centre.postcode}`
                : ''}
            </div>
          )}

          {nearbyQuery.isError ? (
            <div className={styles.empty}>
              {nearbyQuery.error instanceof Error
                ? nearbyQuery.error.message
                : 'Search failed. Check the postcode and try again.'}
            </div>
          ) : showEmptyHelp ? (
            <div className={styles.empty}>
              No results found. This may be because postcode data is still being
              cached — try again after your first search populates the local
              database.
            </div>
          ) : (
            <Card flush>
              <Table<ResultRow>
                columns={columns}
                data={rows}
                keyField={(r) => `${r.kind}-${r.enc_id}`}
                loading={nearbyQuery.isLoading && submitted !== null}
                onRowClick={(r) => setHighlightedEncId(r.enc_id)}
                emptyState={
                  submitted === null ? (
                    <span className={styles.muted}>Enter a postcode and search.</span>
                  ) : null
                }
                ariaLabel="Nearby results"
              />
            </Card>
          )}
        </div>

        <div className={styles.mapPanel}>
          <Card flush>
            {nearbyQuery.isLoading && submitted !== null ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 480 }}>
                <Spinner size="sm" label="Loading…" />
              </div>
            ) : nearbyQuery.data ? (
              <div style={{ height: 'calc(70vh - 1px)', minHeight: 520 }}>
                <NearbyResultsMap
                  centre={[nearbyQuery.data.centre.latitude, nearbyQuery.data.centre.longitude]}
                  radiusMiles={nearbyQuery.data.radius_miles}
                  bookings={nearbyQuery.data.bookings}
                  skipJobs={nearbyQuery.data.skip_jobs}
                  highlightedEncId={highlightedEncId}
                  onSelect={(_kind, encId) => setHighlightedEncId(encId)}
                />
              </div>
            ) : (
              <div className={styles.empty}>
                Enter a postcode above and click Search to see results.
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
